// ── sessions-helpers: module access + display + feature flags + write errors ─
// Wave 6 (Sessions). Shared by the Sessions list / Calendar / session detail /
// Create Series / Clear Series surfaces. Ids are STRINGS (bigint-safe) — passed
// VERBATIM, never Number()'d.
//
// ── PARTICIPANT ISOLATION (blueprint §6 gate #1, FR-PDASH-001) ──────────────
// The ADMIN Sessions module (/sessions, /sessions/:id, /calendar) is for STAFF /
// coach / company_admin / cohort_lead ONLY. A `participant` is DENIED the module
// entirely — their own sessions are the Wave-9 Participant Dashboard, never here.
// This is a fail-closed allowlist: an unknown/new role is denied until it is
// explicitly added. Defense-in-depth at BOTH layers: the route manifest denies
// participant at the route (`/sessions` + `/calendar` use cohortReadRoles ->
// generic-not-found + no nav), AND this page-level guard fail-closes (load-bearing
// for `/sessions/:id`, which keeps allRoles per the cohort-workspace recipe).

import { humanizeLabel } from "@vjal/ui";

import { ApiClientError } from "../../services/api-client";
import {
  honestGateFromError,
  postureForCoachingError,
  referenceFromError
} from "../../services/coaching-error-posture";
import type { CoachingRole } from "../../domain/roles";

// ── Admin Sessions MODULE access (fail-closed allowlist) ─────────────────────
export const SESSIONS_MODULE_ROLES: readonly CoachingRole[] = [
  "super_admin",
  "admin",
  "coach",
  "company_admin",
  "cohort_lead"
];

/** Whether the role may load the ADMIN Sessions module (fail-closed). */
export function canAccessSessionsModule(role: CoachingRole): boolean {
  return SESSIONS_MODULE_ROLES.includes(role);
}

// ── Feature flags (default OFF) ──────────────────────────────────────────────
// Create Series for COACH is approval-gated (FR-SESS — "Create Series only if
// approved (default OFF → gated)"). Default OFF: a coach sees the Create Series
// affordance disabled with an honest "pending approval" reason until an operator
// flips this. SA/Admin are always allowed (their capability gate is enough).
//
// Clear Series (FR-SESS-002) is SA-only + behind its OWN flag AND the backend
// endpoint does not exist yet — so even with the flag on it ships as a
// confirm+reason scaffold that performs NO destructive action (see the capability
// `sessions:clear-series`, which is marked unavailable). These flags are
// build-time/dev placeholders; no production policy is hardcoded.
const COACH_CREATE_SERIES_APPROVED = false;
const CLEAR_SERIES_FLAG_ON = false;

/** Whether COACH create-series is operator-approved (default OFF → gated). */
export function isCoachCreateSeriesApproved(): boolean {
  return COACH_CREATE_SERIES_APPROVED;
}

/** Whether the Clear Series feature flag is on (still endpoint-blocked). */
export function isClearSeriesFlagOn(): boolean {
  return CLEAR_SERIES_FLAG_ON;
}

/**
 * Whether the role may USE Create Series. SA/Admin always (within scope); coach
 * only when approved (the default-OFF flag). Read-only roles never.
 */
export function canUseCreateSeries(role: CoachingRole): boolean {
  if (role === "super_admin" || role === "admin") return true;
  if (role === "coach") return isCoachCreateSeriesApproved();
  return false;
}

/** A non-empty, non-"new" route segment is the only thing that can be a real id. */
export function isValidSessionId(id: string | null | undefined): id is string {
  return typeof id === "string" && id.trim().length > 0 && id !== "new";
}

export function isValidCohortId(id: string | null | undefined): id is string {
  return typeof id === "string" && id.trim().length > 0 && id !== "new";
}

export function sessionDisplayTitle(title: string | null | undefined, id: string): string {
  const trimmed = (title ?? "").trim();
  return trimmed.length > 0 ? trimmed : `Session ${id}`;
}

export function sessionStatusLabel(status: string | null | undefined): string {
  return humanizeLabel(status ?? "scheduled");
}

export function sessionStatusTone(status: string | null | undefined): "success" | "warning" | "danger" | "default" {
  switch (status) {
    case "completed":
      return "success";
    case "cancelled":
    case "archived":
      return "danger";
    case "rescheduled":
    case "pending":
      return "warning";
    default:
      return "default";
  }
}

export function inviteStatusLabel(status: string | null | undefined): string {
  return humanizeLabel(status ?? "not_requested");
}

export function inviteStatusTone(status: string | null | undefined): "success" | "warning" | "default" {
  if (status === "accepted" || status === "delivered" || status === "readiness_recorded") return "success";
  if (status === "not_requested" || status == null || status === "provider_closed") return "warning";
  return "default";
}

/** Neutral display for a missing optional field — never a fabricated value. */
export function orDash(value: string | null | undefined): string {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : "—";
}

// ── Session lifecycle transitions (write-enum, blueprint §1 backend contract) ─
// The /sessions/:id/lifecycle write-enum is completed | cancelled | archived.
// Cancel + archive are DESTRUCTIVE (need a confirm); complete is terminal but
// non-destructive. The read status set is a superset (scheduled/rescheduled/…)
// so we humanize on read but only offer this conservative writable set.
export const SESSION_LIFECYCLE_TRANSITIONS = [
  { status: "completed" as const, label: "Mark complete", destructive: false },
  { status: "cancelled" as const, label: "Cancel session", destructive: true },
  { status: "archived" as const, label: "Archive session", destructive: true }
];

export type SessionLifecycleTransition = (typeof SESSION_LIFECYCLE_TRANSITIONS)[number];

// ── Honest write-error → message + tone (mirrors participants-write) ─────────
export interface WriteErrorView {
  tone: "error" | "warning";
  message: string;
}

/**
 * Map a session write error to an honest, non-leaking message. A closed gate
 * states nothing changed (no false success); a denied write reads as its honest
 * message. The single CO_* → posture map is reused so the modals speak the exact
 * same vocabulary as the list/detail reads.
 */
export function describeSessionWriteError(error: unknown): WriteErrorView | null {
  if (error == null) return null;
  if (!(error instanceof ApiClientError)) {
    return { tone: "error", message: "Couldn't complete the session action. Please try again." };
  }
  const posture = postureForCoachingError(error);
  const reference = referenceFromError(error);
  const ref = reference ? ` (reference: ${reference})` : "";

  if (posture === "permission-denied") {
    return { tone: "error", message: "Your role can't make this change. Ask a super admin if you need it." };
  }
  if (posture === "gate-closed") {
    const gate = honestGateFromError(error);
    return {
      tone: "warning",
      message: `${gate?.reason ?? "This action is closed right now."} No change was made.`
    };
  }
  if (posture === "unavailable") {
    return { tone: "error", message: "Coaching is temporarily unavailable. Try again in a moment." };
  }
  // validation / not-found / generic — show the server message + reference.
  return { tone: "error", message: `${error.message}${ref}` };
}
